import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { IonicModule } from '@ionic/angular';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

/**
 * Loading skeleton for the schedule weekly view. Mirrors the calendar grid
 * (250px property column + 7 day columns, same cell metrics) with placeholder
 * day headers, property name/tree lines, reservation pills and job cards so
 * the page reads as the schedule while it loads.
 *
 * It reuses the weekly view's `.scrollable-container` class so the scroll
 * area (and its thin bottom scrollbar) is identical to the loaded calendar.
 * The cells float in with the same diagonal wave the real calendar used to
 * play; the host page keeps this skeleton mounted until the weekly view
 * reports readiness (see `readinessChange`), then the real grid replaces it
 * in place, instantly.
 */
@Component({
  selector: 'tidy-schedule-weekly-skeleton',
  standalone: true,
  imports: [IonicModule, TidyUiComponentsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./schedule-weekly-skeleton.component.scss'],
  template: `
    <div class="scrollable-container sk-weekly">
      <div class="sk-weekly-grid">
        <div class="sk-weekly-header sk-weekly-prop-header"></div>
        @for (cell of headerCells; track $index) {
          <div class="sk-weekly-header">
            <ion-skeleton-text [animated]="true" class="sk-weekly-day-label" />
          </div>
        }
        @for (row of rows; track $index) {
          <div class="sk-weekly-prop-cell">
            <div class="sk-weekly-prop-name">
              <ion-skeleton-text [animated]="true" class="sk-weekly-tag" />
              <ion-skeleton-text [animated]="true" class="sk-weekly-name" [style.width.px]="row.name" />
            </div>
            <div class="sk-weekly-tree">
              <ion-skeleton-text [animated]="true" class="sk-weekly-tree-line" [style.width.px]="row.tree" />
              <ion-skeleton-text [animated]="true" class="sk-weekly-tree-line" [style.width.px]="row.tree2" />
            </div>
          </div>
          @for (cell of row.cells; track $index) {
            <div class="sk-weekly-day-cell">
              @if (cell.bar) {
                <ion-skeleton-text [animated]="true" class="sk-weekly-res-bar" [style.width.%]="cell.bar" />
              }
              @if (cell.card) {
                <!-- Real tidy-card chrome, like the loaded job cards: icon +
                     title row over a bordered footer with time and status. -->
                <div class="sk-weekly-job-card" [style.margin-top.px]="cell.cardTop ?? null">
                  <tidy-card>
                    <div class="sk-job-top">
                      <ion-skeleton-text [animated]="true" class="sk-job-icon" />
                      <ion-skeleton-text [animated]="true" class="sk-job-title" />
                    </div>
                    <div class="sk-job-footer">
                      <ion-skeleton-text [animated]="true" class="sk-job-time" />
                      <span class="sk-job-status">
                        <ion-skeleton-text [animated]="true" class="sk-job-status-text" />
                        <ion-skeleton-text [animated]="true" class="sk-job-status-icon" />
                      </span>
                    </div>
                  </tidy-card>
                </div>
              }
            </div>
          }
        }
      </div>
    </div>
  `,
})
export class ScheduleWeeklySkeletonComponent {
  readonly headerCells = [0, 1, 2, 3, 4, 5, 6];

  /**
   * One entry per property row. `name`/`tree`/`tree2` are pixel widths for
   * the property-column lines; `cells` lists the 7 day cells, where `bar` is
   * a reservation pill width as % of the cell (>100 overflows into following
   * days, like real multi-day reservation bars), `card` adds a 60px job card
   * and `cardTop` pushes a card below a bar overflowing from an earlier day
   * (mirrors the real calendar's paddingTop stacking — never overlap them).
   * Deterministic so the grid never reshuffles between renders.
   */
  readonly rows: { name: number; tree: number; tree2: number; cells: { bar?: number; card?: boolean; cardTop?: number }[] }[] = [
    { name: 150, tree: 172, tree2: 138, cells: [{ bar: 150, card: true }, {}, {}, {}, {}, { card: true }, { bar: 95 }] },
    { name: 188, tree: 150, tree2: 164, cells: [{ bar: 420 }, { card: true, cardTop: 32 }, {}, {}, {}, { card: true }, {}] },
    { name: 132, tree: 166, tree2: 128, cells: [{ card: true }, {}, { card: true }, {}, { bar: 160 }, {}, {}] },
    { name: 170, tree: 144, tree2: 156, cells: [{ bar: 130 }, {}, { card: true }, {}, { card: true }, {}, { bar: 95 }] },
    { name: 144, tree: 178, tree2: 120, cells: [{}, { bar: 300 }, {}, { card: true, cardTop: 32 }, {}, { bar: 130 }, {}] },
    { name: 162, tree: 136, tree2: 148, cells: [{ card: true }, {}, {}, { bar: 200 }, {}, {}, { card: true }] },
    { name: 178, tree: 158, tree2: 132, cells: [{ bar: 250, card: true }, {}, { card: true, cardTop: 32 }, {}, {}, { bar: 160 }, {}] },
    { name: 140, tree: 168, tree2: 144, cells: [{}, { card: true }, {}, {}, { bar: 130 }, {}, {}] },
    { name: 156, tree: 148, tree2: 160, cells: [{ bar: 95 }, {}, { bar: 300 }, {}, { card: true, cardTop: 32 }, {}, {}] },
    { name: 184, tree: 162, tree2: 126, cells: [{ card: true }, {}, {}, { card: true }, {}, {}, { bar: 95 }] },
    { name: 136, tree: 174, tree2: 152, cells: [{ bar: 520 }, {}, { card: true, cardTop: 32 }, {}, {}, { card: true, cardTop: 32 }, {}] },
    { name: 168, tree: 140, tree2: 136, cells: [{}, { bar: 160 }, {}, {}, { card: true }, {}, {}] },
    { name: 148, tree: 154, tree2: 166, cells: [{ card: true }, {}, { bar: 200 }, {}, { card: true }, {}, {}] },
    { name: 174, tree: 170, tree2: 130, cells: [{ bar: 130, card: true }, {}, {}, {}, {}, { bar: 200 }, {}] },
    { name: 142, tree: 146, tree2: 158, cells: [{}, { card: true }, {}, { bar: 160 }, {}, {}, { card: true }] },
    { name: 160, tree: 164, tree2: 142, cells: [{ bar: 300, card: true }, {}, {}, { card: true }, {}, {}, {}] },
  ];
}
